import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  HelpCircle, FileEdit, ScrollText, Handshake, FileCheck2, BarChart3, X, ArrowRight, Compass,
} from "lucide-react";
import { useTour } from "./TourContext";

const LS_KEY = "stryde_workflow_guide_dismissed_v1";

const STEPS = [
  {
    num: 1,
    icon: FileEdit,
    title: "Receive sponsor draft",
    desc: "When a sponsor sends a new CTA (PDF / Word / Excel), upload it through the Negotiation Worksheet — not the CTA Library. Drafts never feed the benchmark.",
    cta: { label: "Open Negotiation Worksheet", to: "/negotiation" },
    accent: "from-orange-500/10 to-orange-500/0 border-orange-200",
    iconBg: "bg-orange-100 text-orange-700",
  },
  {
    num: 2,
    icon: ScrollText,
    title: "Review contract language",
    desc: "AI automatically reviews every clause against the 23-category Stryde checklist. Open the Contract Review tab inside the negotiation to see flagged escalations with proposed counter-language.",
    accent: "from-purple-500/10 to-purple-500/0 border-purple-200",
    iconBg: "bg-purple-100 text-purple-700",
  },
  {
    num: 3,
    icon: Handshake,
    title: "Negotiate the budget",
    desc: "In the Budget Editor tab, edit the Stryde Counter column side-by-side against Sponsor Proposed. Each row shows inline P75/P80/P90 percentiles from your benchmark — click any percentile chip to match.",
    accent: "from-blue-500/10 to-blue-500/0 border-blue-200",
    iconBg: "bg-blue-100 text-blue-700",
  },
  {
    num: 4,
    icon: FileCheck2,
    title: "Finalize & promote to signed",
    desc: "When the deal closes, click \"Finalize & Promote\" — your counter values become a SIGNED CTA in the library and instantly join the benchmark pool. The negotiation locks as completed.",
    accent: "from-emerald-500/10 to-emerald-500/0 border-emerald-200",
    iconBg: "bg-emerald-100 text-emerald-700",
  },
  {
    num: 5,
    icon: BarChart3,
    title: "Benchmark from signed only",
    desc: "Rate Card & Benchmark automatically pull from signed CTAs only — never drafts. Every future negotiation gets sharper percentiles as your library grows.",
    accent: "from-cyan-500/10 to-cyan-500/0 border-cyan-200",
    iconBg: "bg-cyan-100 text-cyan-700",
  },
];

const TIPS = [
  "Re-uploading the same sponsor + study triggers a duplicate prompt — you can open the existing draft or create a new round.",
  "Edit a CTA's status in the CTA Library to flip between Signed and Draft at any time.",
  "Use the \"+ Add new site\" inline button when negotiating for a site that isn't in your list yet.",
  "Export any negotiation to XLSX, or any contract review to Word — handy for sharing with sponsor counsel.",
];

/**
 * Workflow guide — auto-opens on first login per browser, or on demand via the (?) button.
 */
export default function WorkflowGuide({ open, onOpenChange }) {
  const nav = useNavigate();
  const tour = useTour();
  const [dontShow, setDontShow] = useState(false);

  // Load saved preference each time the dialog opens
  useEffect(() => {
    if (open) {
      setDontShow(localStorage.getItem(LS_KEY) === "1");
    }
  }, [open]);

  const close = () => {
    if (dontShow) localStorage.setItem(LS_KEY, "1");
    else localStorage.removeItem(LS_KEY);
    onOpenChange(false);
  };

  const goTo = (to) => {
    close();
    nav(to);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="workflow-guide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <HelpCircle className="w-5 h-5 text-[#0A4C95]" />
            How Stryde Finance works
          </DialogTitle>
          <DialogDescription>
            A 5-step playbook from sponsor draft → signed CTA → benchmarked rate card.
          </DialogDescription>
        </DialogHeader>

        {/* Workflow steps */}
        <div className="space-y-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isLast = i === STEPS.length - 1;
            return (
              <div key={s.num} className="relative">
                <div className={`flex gap-4 border rounded-md bg-gradient-to-r ${s.accent} p-4`} data-testid={`wf-step-${s.num}`}>
                  <div className={`${s.iconBg} w-11 h-11 rounded-md flex-shrink-0 flex items-center justify-center font-bold`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-white text-[10.5px] uppercase tracking-wider font-semibold">Step {s.num}</Badge>
                      <h4 className="font-semibold text-slate-900 text-[15px]">{s.title}</h4>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-1.5">{s.desc}</p>
                    {s.cta && (
                      <button
                        onClick={() => goTo(s.cta.to)}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#0A4C95] hover:underline"
                        data-testid={`wf-step-${s.num}-cta`}
                      >
                        {s.cta.label} <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                {!isLast && (
                  <div className="flex justify-center my-1" aria-hidden>
                    <div className="w-px h-3 bg-slate-300" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pro tips */}
        <div className="border-t border-slate-200 pt-4 mt-2">
          <h5 className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Pro tips</h5>
          <ul className="space-y-1.5">
            {TIPS.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-700">
                <span className="w-1 h-1 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="border-t border-slate-200 pt-4 flex sm:items-center justify-between sm:justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              className="rounded border-slate-300 text-[#0A4C95] focus:ring-[#0A4C95]"
              data-testid="wf-dont-show"
            />
            Don&apos;t show this again on login
          </label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { close(); tour.start(); }}
              className="border-[#F05D23] text-[#F05D23] hover:bg-orange-50"
              data-testid="wf-start-tour-btn"
            >
              <Compass className="w-4 h-4 mr-1.5" /> Start live tour
            </Button>
            <Button onClick={close} className="bg-[#0A4C95] hover:bg-[#083d79]" data-testid="wf-close-btn">
              Got it
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook that auto-opens the guide on first login of a browser session (unless dismissed).
 * Returns { open, setOpen } for parent to wire up.
 */
export function useWorkflowGuide() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(LS_KEY) === "1";
    const sessionSeen = sessionStorage.getItem("stryde_workflow_seen_this_session") === "1";
    if (!dismissed && !sessionSeen) {
      // Defer slightly so the page first paints
      const t = setTimeout(() => {
        setOpen(true);
        sessionStorage.setItem("stryde_workflow_seen_this_session", "1");
      }, 600);
      return () => clearTimeout(t);
    }
  }, []);

  return { open, setOpen };
}
